import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";

let socketInstance = null;
let healthCheckInterval = null;
let lastPongTime = Date.now();
let isSocketHealthy = true;
let isRefreshingToken = false;
let consecutiveRefreshFailures = 0;
let sessionAbandoned = false;

const MAX_REFRESH_FAILURES = 4;
const REFRESH_RETRY_BASE_DELAY = 4000;

// Tenta renovar o token e reconectar o socket com backoff crescente.
// Usada tanto quando a conexão falha explicitamente com erro de JWT
// (connect_error) quanto quando o servidor derruba a conexão já
// estabelecida por token inválido (disconnect "io server disconnect") —
// o backend só valida o JWT depois do handshake, então o segundo caso é
// o mais comum na prática.
const attemptRefreshAndReconnect = async (socket, previousToken) => {
    if (isRefreshingToken) return;
    isRefreshingToken = true;

    console.warn(`[SOCKET] ⚠️ Sessão com token expirado. Tentando refresh...`);

    try {
        const response = await fetch(getBackendUrl() + "/auth/refresh_token", {
            method: "POST",
            credentials: "include"
        });
        const data = await response.json();

        if (response.ok && data.token) {
            consecutiveRefreshFailures = 0;
            localStorage.setItem("token", JSON.stringify(data.token));
            console.log(`[SOCKET] ✅ Token renovado. Reconectando socket...`);
            socket.io.opts.query = { token: data.token };
            socket.connect();
        } else {
            throw new Error("Refresh token inválido ou expirado");
        }
    } catch (err) {
        console.warn(`[SOCKET] ❌ Refresh falhou.`);
        const currentToken = localStorage.getItem("token");

        // Se o usuário já fez login com novas credenciais durante a tentativa
        // de refresh (token no localStorage mudou), reconecta com o token novo
        // em vez de contar como falha.
        if (currentToken && currentToken !== previousToken) {
            console.warn(`[SOCKET] Token novo encontrado (novo login). Reconectando com ele.`);
            consecutiveRefreshFailures = 0;
            socket.io.opts.query = { token: JSON.parse(currentToken) };
            socket.connect();
        } else if (!currentToken || consecutiveRefreshFailures >= MAX_REFRESH_FAILURES - 1) {
            // Sessão sem token válido, ou já tentamos renovar demais vezes seguidas —
            // desiste em vez de martelar /auth/refresh_token + reconexão sem parar.
            console.warn(`[SOCKET] Desistindo de reconectar — sessão inválida.`);
            sessionAbandoned = true;
            consecutiveRefreshFailures = 0;
            localStorage.removeItem("token");
            socket.io.opts.reconnection = false;
            if (healthCheckInterval) {
                clearInterval(healthCheckInterval);
                healthCheckInterval = null;
            }
            window.location.href = "/login";
        } else {
            consecutiveRefreshFailures += 1;
            const backoff = REFRESH_RETRY_BASE_DELAY * consecutiveRefreshFailures;
            console.warn(`[SOCKET] Nova tentativa em ${backoff}ms (falha ${consecutiveRefreshFailures}/${MAX_REFRESH_FAILURES}).`);
            setTimeout(() => {
                if (!sessionAbandoned && localStorage.getItem("token")) {
                    socket.connect();
                }
            }, backoff);
        }
    } finally {
        isRefreshingToken = false;
    }
};

const connectToSocket = () => {
    // Retorna a instância existente independente do estado de conexão.
    // Criar uma nova instância enquanto o socket reconecta mata os handlers
    // já registrados nos componentes (bug de referência estale).
    if (socketInstance) {
        return socketInstance;
    }

    sessionAbandoned = false;
    consecutiveRefreshFailures = 0;

    try {
        const token = localStorage.getItem("token");
        if (!token) {
            console.warn("Token não encontrado, socket não será inicializado");
            return null;
        }

        let parsedToken;
        try {
            parsedToken = JSON.parse(token);
        } catch (err) {
            console.error("Token inválido no localStorage");
            return null;
        }

        try {
            const tokenData = JSON.parse(atob(parsedToken.split('.')[1]));
            if (tokenData.exp * 1000 < Date.now()) {
                console.warn("Token expirado, socket não será inicializado");
                localStorage.removeItem("token");
                return null;
            }
        } catch (err) {
            console.error("Erro ao verificar expiração do token");
            return null;
        }

        const socket = openSocket(getBackendUrl(), {
            transports: ["polling", "websocket"],
            query: {
                token: parsedToken
            },
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 30000,
            reconnectionAttempts: Infinity,
            forceNew: false,
            timeout: 10000
        });

        socket.on("connect", () => {
            const timestamp = new Date().toISOString();

            const user = JSON.parse(localStorage.getItem("user") || "{}");
            if (user && user.id) {
                socket.emit("getTickets", { userId: user.id });
                socket.emit("userStatus", { userId: user.id, status: "online" });
                socket.emit("subscribeTicketCounter");
                socket.emit("joinNotification");
            } else {
                console.warn(`[FRONT_SOCKET_NO_USER][${timestamp}] Não foi possível sincronizar tickets: usuário não encontrado no localStorage`);
            }
        });


        socket.on("connect_error", async (error) => {
            console.error(`[SOCKET] ❌ ERRO DE CONEXÃO:`, error.message);

            if (error.message.includes("jwt expired") ||
                error.message.includes("invalid token") ||
                error.message.includes("jwt malformed")) {
                socket.disconnect();
                await attemptRefreshAndReconnect(socket, token);
            }
        });

        socket.on("reconnect", (attemptNumber) => {
            const timestamp = new Date().toISOString();

            const user = JSON.parse(localStorage.getItem("user") || "{}");
            if (user && user.id) {
                socket.emit("getTickets", { userId: user.id });
                socket.emit("subscribeTicketCounter");
            } else {
                console.warn(`[FRONT_SOCKET_RESYNC_FAILED][${timestamp}] Não foi possível resincronizar tickets após reconexão: usuário não encontrado`);
            }
        });

        socket.on("disconnect", (reason) => {
            console.warn(`[SOCKET] ⚠️ DESCONECTADO! Motivo: ${reason}`);

            if (reason === 'transport error' || reason === 'transport close') {
                console.warn(`[SOCKET] Erro de transporte. Tentando reconectar...`);
            }

            if (reason === 'io server disconnect') {
                // O backend só valida o JWT depois do handshake — se o token
                // estiver expirado, ele aceita a conexão e derruba em seguida
                // com esse motivo. Reconectar direto com o mesmo token só
                // repetiria o ciclo a cada 1s para sempre, então passa pelo
                // fluxo de refresh (com backoff e desistência) em vez de
                // conectar de novo às cegas.
                console.warn(`[SOCKET] Servidor fechou a conexão. Tentando renovar sessão...`);
                attemptRefreshAndReconnect(socket, token);
            }
        });

        startHealthCheck(socket);

        socketInstance = socket;
        return socket;
    } catch (err) {
        console.error("Erro ao conectar socket:", err);
        return null;
    }
};

const startHealthCheck = (socket) => {
    if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
    }

    socket.on("pong", () => {
        lastPongTime = Date.now();
        isSocketHealthy = true;
    });

    healthCheckInterval = setInterval(() => {
        socket.emit("ping");

        const now = Date.now();
        if (now - lastPongTime > 15000) {
            isSocketHealthy = false;

            if (socket && !socket.connected && !sessionAbandoned && !isRefreshingToken && localStorage.getItem("token")) {
                socket.connect();
            }
        }
    }, 10000);
};

const reconnectSocket = () => {
    if (socketInstance) {
        socketInstance.disconnect();
    }
    return connectToSocket();
};

export default connectToSocket;
export { reconnectSocket };
